/**
 * LivenessChallengeView — Component for active liveness challenges.
 * Shows a random challenge (blink, turn left/right, smile) with a 5s timer.
 * Provides visual feedback of challenge progress.
 *
 * Validates: Requirements 2.2, 2.3, 2.4
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { Eye, SmilePlus, ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { ChallengeType } from '../../hooks/useLivenessDetection';
import type * as faceapi from 'face-api.js';

export interface LivenessChallengeViewProps {
  challengeType: ChallengeType;
  onCompleted: (score: number) => void;
  onTimeout: () => void;
  landmarks: faceapi.FaceLandmarks68 | null;
  expressions: faceapi.FaceExpressions | null;
}

const CHALLENGE_TIMEOUT_MS = 5000;

const challengeConfig: Record<ChallengeType, {
  label: string;
  instruction: string;
  icon: typeof Eye;
}> = {
  [ChallengeType.BLINK]: {
    label: 'Piscar',
    instruction: 'Pisque os olhos',
    icon: Eye,
  },
  [ChallengeType.TURN_LEFT]: {
    label: 'Virar Esquerda',
    instruction: 'Vire o rosto para a esquerda',
    icon: ArrowLeft,
  },
  [ChallengeType.TURN_RIGHT]: {
    label: 'Virar Direita',
    instruction: 'Vire o rosto para a direita',
    icon: ArrowRight,
  },
  [ChallengeType.SMILE]: {
    label: 'Sorrir',
    instruction: 'Sorria para a câmera',
    icon: SmilePlus,
  },
};

// EAR threshold for blink detection
const EAR_THRESHOLD = 0.21;
// Smile threshold
const SMILE_THRESHOLD = 0.7;
// Head turn threshold in degrees
const HEAD_TURN_THRESHOLD = 15;

function calculateEAR(eyePoints: { x: number; y: number }[]): number {
  if (eyePoints.length < 6) return 1;
  const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
  const vertical1 = dist(eyePoints[1], eyePoints[5]);
  const vertical2 = dist(eyePoints[2], eyePoints[4]);
  const horizontal = dist(eyePoints[0], eyePoints[3]);
  if (horizontal === 0) return 1;
  return (vertical1 + vertical2) / (2 * horizontal);
}

function calculateYawAngle(landmarks: faceapi.FaceLandmarks68): number {
  const positions = landmarks.positions;
  const noseTip = positions[30];
  const leftContour = positions[0];
  const rightContour = positions[16];
  if (!noseTip || !leftContour || !rightContour) return 0;
  const faceCenterX = (leftContour.x + rightContour.x) / 2;
  const faceWidth = rightContour.x - leftContour.x;
  if (faceWidth === 0) return 0;
  const noseOffset = (noseTip.x - faceCenterX) / (faceWidth / 2);
  const yawRadians = Math.asin(Math.max(-1, Math.min(1, noseOffset)));
  return (yawRadians * 180) / Math.PI;
}

export function LivenessChallengeView({
  challengeType,
  onCompleted,
  onTimeout,
  landmarks,
  expressions,
}: LivenessChallengeViewProps) {
  const [timeRemaining, setTimeRemaining] = useState(CHALLENGE_TIMEOUT_MS);
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);

  const startTimeRef = useRef(Date.now());
  const completedRef = useRef(false);

  const config = challengeConfig[challengeType];
  const Icon = config.icon;

  // Check if challenge is completed based on current landmarks/expressions
  const checkChallenge = useCallback((): boolean => {
    if (!landmarks || !expressions) return false;

    switch (challengeType) {
      case ChallengeType.BLINK: {
        const positions = landmarks.positions;
        const leftEye = [positions[36], positions[37], positions[38], positions[39], positions[40], positions[41]];
        const rightEye = [positions[42], positions[43], positions[44], positions[45], positions[46], positions[47]];
        const leftEAR = calculateEAR(leftEye);
        const rightEAR = calculateEAR(rightEye);
        const avgEAR = (leftEAR + rightEAR) / 2;
        return avgEAR < EAR_THRESHOLD;
      }
      case ChallengeType.SMILE: {
        const happy = expressions.happy ?? 0;
        return happy > SMILE_THRESHOLD;
      }
      case ChallengeType.TURN_LEFT: {
        const yaw = calculateYawAngle(landmarks);
        return yaw < -HEAD_TURN_THRESHOLD;
      }
      case ChallengeType.TURN_RIGHT: {
        const yaw = calculateYawAngle(landmarks);
        return yaw > HEAD_TURN_THRESHOLD;
      }
      default:
        return false;
    }
  }, [challengeType, landmarks, expressions]);

  // Timer countdown
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, CHALLENGE_TIMEOUT_MS - elapsed);
      setTimeRemaining(remaining);
      setProgress(elapsed / CHALLENGE_TIMEOUT_MS);

      if (remaining <= 0 && !completedRef.current) {
        onTimeout();
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [onTimeout]);

  // Check challenge completion on each landmarks/expressions update
  useEffect(() => {
    if (completedRef.current) return;

    const isCompleted = checkChallenge();
    if (isCompleted) {
      completedRef.current = true;
      setCompleted(true);

      const elapsed = Date.now() - startTimeRef.current;
      const score = Math.max(0, 1 - elapsed / CHALLENGE_TIMEOUT_MS);
      onCompleted(score);
    }
  }, [landmarks, expressions, checkChallenge, onCompleted]);

  const timerSeconds = Math.ceil(timeRemaining / 1000);
  const timerColor = timerSeconds <= 2 ? 'text-red-400' : 'text-white';

  return (
    <div
      className="flex flex-col items-center gap-4 p-4"
      role="region"
      aria-label={`Desafio de liveness: ${config.instruction}`}
      aria-live="polite"
    >
      {/* Challenge icon and instruction */}
      <div className={`
        flex flex-col items-center gap-3 p-6 rounded-2xl transition-all duration-300
        ${completed ? 'bg-green-900/40 border border-green-500/50' : 'bg-surface-800/60 border border-surface-700'}
      `}>
        <div className={`
          w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-300
          ${completed ? 'bg-green-500/20 text-green-400' : 'bg-primary-500/20 text-primary-400'}
        `}>
          <Icon className="w-8 h-8" />
        </div>

        <p className="text-white text-lg font-medium text-center">
          {config.instruction}
        </p>

        {completed && (
          <p className="text-green-400 text-sm font-medium">
            ✓ Desafio concluído!
          </p>
        )}
      </div>

      {/* Timer */}
      {!completed && (
        <div className="flex items-center gap-2">
          <Clock className={`w-4 h-4 ${timerColor}`} />
          <span className={`text-sm font-mono ${timerColor}`}>
            {timerSeconds}s restantes
          </span>
        </div>
      )}

      {/* Progress bar */}
      <div className="w-full max-w-xs h-2 bg-surface-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-100 ${
            completed ? 'bg-green-500' : progress > 0.8 ? 'bg-red-500' : 'bg-primary-500'
          }`}
          style={{ width: `${Math.min(progress * 100, 100)}%` }}
          role="progressbar"
          aria-valuenow={Math.round(progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Tempo restante para o desafio"
        />
      </div>
    </div>
  );
}
